import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import CategoryBrowseModal from '../../../../components/CategoryBrowseModal/CategoryBrowseModal';
import { useCategories } from '../../../../hooks/useCategories';
import { useToast } from '../../../../contexts/ToastContext';
import { queryKeys } from '../../../../lib/queryKeys';
import type { Category } from '../../../../types/api-responses';
import styles from './Section.module.css';
import s from './CategoriesSection.module.css';

/**
 * CR-2026-05-15 — Categories sub-section (My Business shell).
 *
 * Drives the marketplace taxonomy that does five jobs in Devanza:
 *   1. Search & filter on the consumer marketplace ("salons that do Henna")
 *   2. Badge row on the salon's public profile
 *   3. Service catalog grouping inside the Business Portal
 *   4. SEO landing pages owned by marketing
 *   5. Future: paid add-on for "Featured in this category"
 *
 * Owners come here AFTER setting up Branches and BEFORE adding Services — a
 * service must belong to a category that the business is subscribed to.
 *
 * The educational hero at the top explains all of the above so an owner
 * understands why this step matters before they click Browse. After the first
 * successful subscribe, the hero collapses into a thin "Why again?" link
 * (state persisted via localStorage so the hero stays out of the way on
 * subsequent visits).
 *
 * BE contract:
 *   - GET /category/business            → subscribed list
 *   - GET /category                     → master list (all active)
 *   - POST /category/business/{id}      → subscribe (idempotent)
 *   - DELETE /category/business/{id}    → unsubscribe
 *
 * AB#1872 — the owner "request a custom category" flow was removed; owners are
 * subscribe-only against the SuperAdmin-managed master taxonomy.
 */

const HERO_DISMISS_KEY = 'devanza:my-business:categories-hero-dismissed';

function isHeroDismissed(): boolean {
    try {
        return window.localStorage.getItem(HERO_DISMISS_KEY) === '1';
    } catch {
        return false;
    }
}

function setHeroDismissed(value: boolean) {
    try {
        if (value) {
            window.localStorage.setItem(HERO_DISMISS_KEY, '1');
        } else {
            window.localStorage.removeItem(HERO_DISMISS_KEY);
        }
    } catch {
        // ignore quota / disabled storage
    }
}

const CategoriesSection: React.FC = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    const {
        getBusinessCategories,
        getCategories,
        subscribeBusinessCategory,
        removeCategoryFromBusiness,
        loading,
    } = useCategories();

    const [subscribed, setSubscribed] = useState<Category[] | null>(null);
    const [master, setMaster] = useState<Category[] | null>(null);
    const [fetchError, setFetchError] = useState<string | null>(null);

    const [heroDismissed, setHeroDismissedState] = useState<boolean>(isHeroDismissed);
    const [browseOpen, setBrowseOpen] = useState(false);

    const invalidateCategoryCaches = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.categories.business() });
    }, [queryClient]);

    const loadSubscribed = useCallback(async () => {
        setFetchError(null);
        try {
            const bus = await getBusinessCategories();
            setSubscribed((bus?.categories ?? []).filter(c => c.isActive));
        } catch (err) {
            const msg = (err as Error)?.message || 'Failed to load categories';
            setFetchError(msg);
            if (subscribed === null) showToast(msg, 'error');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getBusinessCategories, showToast]);

    const loadMaster = useCallback(async () => {
        if (master !== null) return;
        try {
            const res = await getCategories();
            setMaster((res?.categories ?? []).filter(c => c.isActive));
        } catch (err) {
            const msg = (err as Error)?.message || 'Failed to load master categories';
            showToast(msg, 'error');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getCategories, showToast]);

    useEffect(() => {
        loadSubscribed();
    }, [loadSubscribed]);

    /* ── Hero handlers ─────────────────────────────────────────────── */
    const dismissHero = () => {
        setHeroDismissed(true);
        setHeroDismissedState(true);
    };
    const reopenHero = () => {
        setHeroDismissed(false);
        setHeroDismissedState(false);
    };

    /* ── Browse modal handlers ─────────────────────────────────────── */
    const openBrowse = async () => {
        await loadMaster();
        setBrowseOpen(true);
    };

    const handleSubscribe = async (categoryId: number) => {
        try {
            await subscribeBusinessCategory(categoryId);
            invalidateCategoryCaches();
            await loadSubscribed();
            // First-subscribe collapses the hero so subsequent visits go
            // straight to the working UX. Owner can reopen via "Why again?".
            if (!heroDismissed && (subscribed?.length ?? 0) === 0) {
                dismissHero();
            }
        } catch {
            showToast("Couldn't add that category. Try again.", 'error');
        }
    };

    const handleRemove = async (row: Category) => {
        const ok = window.confirm(
            `Customers won't find your salon under "${row.name}" anymore. Continue?`,
        );
        if (!ok) return;
        try {
            await removeCategoryFromBusiness(row.id);
            showToast(`Removed "${row.name}".`, 'success');
            invalidateCategoryCaches();
            await loadSubscribed();
        } catch {
            showToast("Couldn't remove that category. Try again.", 'error');
        }
    };

    /* ── Derived view state ────────────────────────────────────────── */
    const isInitialLoading = loading && subscribed === null;
    const subscribedIds = useMemo(
        () => new Set((subscribed ?? []).map(c => c.id)),
        [subscribed],
    );
    const showEmpty = !isInitialLoading && !fetchError && (subscribed?.length ?? 0) === 0;
    const showList = !isInitialLoading && !fetchError && (subscribed?.length ?? 0) > 0;
    const showHero = !heroDismissed;

    return (
        <div>
            <header className={styles.header}>
                <h1>Categories</h1>
                <p>
                    Categories tell customers what your salon offers. They drive every
                    marketplace search filter, your public profile badges, and how your
                    services are grouped — set them up before adding services.
                </p>
            </header>

            {/* Educational hero — collapsible, persisted via localStorage */}
            {showHero && (
                <div className={s.heroCard}>
                    <button
                        type="button"
                        className={s.heroCollapse}
                        onClick={dismissHero}
                        aria-label="Hide explanation"
                        title="Hide explanation"
                    >
                        ×
                    </button>
                    <div className={s.heroHeadlineRow}>
                        <div className={s.heroIcon} aria-hidden="true">🏷️</div>
                        <h2 className={s.heroHeadline}>Categories help customers find you</h2>
                    </div>
                    <p className={s.heroBody}>
                        Pick the categories that match what your salon does best. Each one
                        becomes a place customers can find you — in search filters, on your
                        profile, and in Devanza's marketing.
                    </p>
                    <ul className={s.heroBenefits}>
                        <li className={s.heroBenefit}>
                            <span className={s.heroBenefitIcon} aria-hidden="true">🔍</span>
                            <div className={s.heroBenefitText}>
                                <strong>Marketplace search</strong>
                                <span>Customers filtering "Hair Styling" or "Henna" see your salon in the results.</span>
                            </div>
                        </li>
                        <li className={s.heroBenefit}>
                            <span className={s.heroBenefitIcon} aria-hidden="true">🪪</span>
                            <div className={s.heroBenefitText}>
                                <strong>Profile badges</strong>
                                <span>Your picked categories show as badges on your salon's public profile.</span>
                            </div>
                        </li>
                        <li className={s.heroBenefit}>
                            <span className={s.heroBenefitIcon} aria-hidden="true">📈</span>
                            <div className={s.heroBenefitText}>
                                <strong>Marketing reach</strong>
                                <span>Devanza runs category-specific campaigns; salons in those categories get the spillover.</span>
                            </div>
                        </li>
                    </ul>
                    <div className={s.heroPreview}>
                        <span className={s.heroPreviewLabel}>How you'll appear:</span>
                        {(subscribed?.length ?? 0) > 0 ? (
                            subscribed!.slice(0, 5).map(c => (
                                <span key={c.id} className={s.heroPreviewChip}>{c.name}</span>
                            ))
                        ) : (
                            <span className={s.heroPreviewEmpty}>
                                Pick your first category to see the preview chips appear here.
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* "Why again?" link when hero is collapsed */}
            {!showHero && (
                <button type="button" className={s.heroReopenLink} onClick={reopenHero}>
                    Why categories matter — show again
                </button>
            )}

            {/* Loading skeleton */}
            {isInitialLoading && (
                <div aria-busy="true">
                    {[0, 1, 2].map(i => (
                        <div key={i} className={s.skeletonCard} style={{ marginBottom: 10 }}>
                            <div className={`${s.skeletonLine} ${s.skeletonLineMed}`} />
                            <div className={`${s.skeletonLine} ${s.skeletonLineShort}`} />
                        </div>
                    ))}
                </div>
            )}

            {/* Error banner with retry */}
            {fetchError && subscribed === null && (
                <div className={styles.intro} role="alert">
                    <span className={styles.introIcon}>⚠️</span>
                    <div>
                        <strong>Couldn't load your categories.</strong> {fetchError}{' '}
                        <button
                            type="button"
                            onClick={loadSubscribed}
                            className={styles.btn}
                            style={{ marginInlineStart: 12 }}
                        >
                            Try again
                        </button>
                    </div>
                </div>
            )}

            {/* Empty state — owner has zero subscribed categories */}
            {showEmpty && (
                <div className={s.empty}>
                    <div className={s.emptyIcon} aria-hidden="true">🏷️</div>
                    <h2 className={s.emptyTitle}>Pick your first category</h2>
                    <p className={s.emptyDesc}>
                        Start with the one your salon is best known for — you can add more
                        anytime. We'll show you the master list with regional favourites
                        like Moroccan Bath and Henna alongside Hair, Nails, and Spa.
                    </p>
                    <button
                        type="button"
                        className={styles.btnPrimary}
                        onClick={openBrowse}
                    >
                        + Browse categories
                    </button>
                </div>
            )}

            {/* Populated list */}
            {showList && (
                <>
                    <div className={s.toolbar}>
                        <div>
                            <span className={s.count}>
                                {subscribed!.length}{' '}
                                {subscribed!.length === 1 ? 'category' : 'categories'}
                            </span>
                        </div>
                        <button
                            type="button"
                            className={styles.btnPrimary}
                            onClick={openBrowse}
                        >
                            + Browse categories
                        </button>
                    </div>

                    <ul className={s.cardList}>
                        {subscribed!.map(row => (
                            <li key={row.id} className={s.card}>
                                <div className={s.cardThumb}>
                                    {row.imageUrl ? (
                                        <img src={row.imageUrl} alt="" />
                                    ) : (
                                        <span aria-hidden="true">🏷️</span>
                                    )}
                                </div>
                                <div className={s.cardMain}>
                                    <div className={s.cardName}>{row.name}</div>
                                    <div className={s.cardMeta}>
                                        Customers will find you under this category
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className={s.cardRemove}
                                    onClick={() => handleRemove(row)}
                                    aria-label={`Remove ${row.name}`}
                                    title="Remove"
                                >
                                    ×
                                </button>
                            </li>
                        ))}
                    </ul>
                </>
            )}

            {browseOpen && (
                <CategoryBrowseModal
                    isOpen={browseOpen}
                    onClose={() => setBrowseOpen(false)}
                    master={master ?? []}
                    subscribedIds={subscribedIds}
                    onSubscribe={handleSubscribe}
                    loading={loading}
                />
            )}
        </div>
    );
};

export default CategoriesSection;
